import { useEffect } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import { captureWelcomeViewed } from '@/lib/analytics'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { usePublicSignupSurvey } from '../api/public-signup-survey'
import { usePublicSurveyCode } from '../api/public-survey-code'
import { normalizeSurveyPublicCode } from '../lib/survey-public-code'
import { InstrumentLinkBlockedDialog } from '../components/InstrumentLinkBlockedDialog'
import { InstrumentLinkLoadingView } from '../components/InstrumentLinkOnboardingViews'
import { SignupSurveyWelcomeView } from '../components/SignupSurveyWelcomeView'
import {
  SIGNUP_SURVEY_SHARE_BLOCKED_HEADLINE,
  SIGNUP_SURVEY_SHARE_BLOCKED_MESSAGE,
} from '../lib/signup-survey-share'
import { nonSignupSurveyEntryPath } from '../lib/instrument-link-routing'
import { isPublicSignupSurveyNotFound } from '../lib/signup-survey-welcome-copy'

function SignupSurveyBlockedView() {
  return (
    <Box sx={{ width: '100%' }} data-testid="signup-survey-share-blocked">
      <InstrumentLinkBlockedDialog
        open
        headline={SIGNUP_SURVEY_SHARE_BLOCKED_HEADLINE}
        message={SIGNUP_SURVEY_SHARE_BLOCKED_MESSAGE}
      />
    </Box>
  )
}

function CampaignSurveyCodeEntry({ code }: { readonly code: string }) {
  const query = usePublicSurveyCode(code)

  if (query.isPending) {
    return <InstrumentLinkLoadingView />
  }

  if (query.isError || !query.data?.instrumentId) {
    return (
      <Box sx={{ width: '100%' }} data-testid="survey-public-code-missing">
        <InstrumentLinkBlockedDialog
          open
          headline="Survey link not found"
          message="This survey link is not available."
        />
      </Box>
    )
  }

  return <Navigate to={`/instrument/${query.data.instrumentId}/info`} replace />
}

/** Public `/instruments/:id` or `/s/:slug`: signup survey welcome, otherwise instrument info. */
export function SignupSurveySharePage() {
  const { slug, instrumentId } = useParams<{ slug?: string; instrumentId?: string }>()
  const publicCode = slug && !instrumentId ? normalizeSurveyPublicCode(slug) : null
  if (publicCode) {
    return <CampaignSurveyCodeEntry code={publicCode} />
  }
  return <SignupSurveyShareBody slug={slug} instrumentId={instrumentId} />
}

function SignupSurveyShareBody({
  slug,
  instrumentId,
}: {
  readonly slug?: string
  readonly instrumentId?: string
}) {
  const navigate = useNavigate()
  const query = usePublicSignupSurvey({ slug, instrumentId })
  useDocumentTitle(query.data?.name?.trim() || 'Sign-up Survey')

  const welcomeSurveyId =
    query.data?.startable && (query.data.entryToken?.trim().length ?? 0) >= 10
      ? query.data.instrumentId
      : null
  const welcomeRewardAmount = query.data?.reward?.amount ?? null
  useEffect(() => {
    if (!welcomeSurveyId) return
    captureWelcomeViewed({ surveyId: welcomeSurveyId, rewardAmount: welcomeRewardAmount })
  }, [welcomeSurveyId, welcomeRewardAmount])

  if (query.isPending) {
    return <InstrumentLinkLoadingView />
  }

  if (instrumentId && query.isError && isPublicSignupSurveyNotFound(query.error)) {
    return <Navigate to={nonSignupSurveyEntryPath(instrumentId)} replace />
  }

  const resolved = query.data
  if (
    query.isError ||
    !resolved?.startable ||
    !resolved.entryToken ||
    resolved.entryToken.trim().length < 10
  ) {
    return <SignupSurveyBlockedView />
  }

  return (
    <SignupSurveyWelcomeView
      survey={resolved}
      onStarted={(id) => {
        navigate(`/instrument/${id}/info`, { replace: true })
      }}
    />
  )
}
